import { AfterViewInit, Component, Input, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { AuthService } from "app/auth/auth.service";
import { Observable } from "rxjs/Observable";
import { of } from "rxjs/observable/of";
import { catchError, map } from "rxjs/operators";
import SortOptions from "../../../../models/content-wrapper/sort-options";
import CompanyResourceSummary from "../../../../models/resource-management/company-resource-summary";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";

interface DropdownItem {
  label: string;
  value: number;
}

/**
 * Component to wrap a searchable select input for provider lookup with credential filtering.
 * Pre-loads all valid providers and filters client-side.
 * Filters results to show only system users with valid MD/NP credentials.
 */
@Component({
  selector: "fixed-label-search-select",
  templateUrl: "fixed-label-search-select.component.html",
})
export class FixedLabelSearchSelectComponent implements OnInit, AfterViewInit {
  // Configuration properties
  @Input() group: FormGroup;
  @Input() config;

  // Internal data model
  id: string;
  componentId: string;
  label: any;
  placeholder: string = "Search";
  required: boolean;
  disabled: boolean;
  readonly: boolean = false;
  hidden: boolean = false;
  hideIfDisabled: boolean = false;

  // Provider data
  items: DropdownItem[] = [];
  allProviders: CompanyResourceSummary[] = [];
  selectedItemId: number | null = null;

  // UI state
  isLoading: boolean = false;
  shouldFilterByCredentials: boolean = false; // Default to false for backward compatibility
  private hasExternalDataLoaded: boolean = false; // Track when external data has been loaded from API

  // Allowed credential types: MD, DO, FNP-BC, ANP-BC, ACHPN, CRNP, APRN
  private readonly ALLOWED_CREDENTIALS = [
    "MD",
    "DO",
    "FNPBC",
    "ANPBC",
    "ACHPN",
    "CRNP",
    "APRN",
  ];

  constructor(
    public formBuilder: FormBuilder,
    private resourceManagementService: ResourceManagementService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.id = this.config.id;
    this.componentId = this.config.componentId;
    this.label =
      this.config.label !== undefined ? this.config.label : this.config.id;
    if (this.label === "false") {
      this.label = false;
    }
    this.readonly = this.config.readonly;
    if (this.config.placeholder) {
      this.placeholder = this.config.placeholder;
    }
    if (this.config.hidden) {
      this.hidden = this.config.hidden;
    }

    // Initialize credential filtering flag
    if (this.config.shouldFilterByCredentials !== undefined) {
      this.shouldFilterByCredentials = this.config.shouldFilterByCredentials;
    }

    this.disabled = this.group.controls[this.id].disabled;

    this.composeValidators();

    const selfControl = this.group.controls[this.id];
    selfControl.valueChanges.subscribe((data) => {
      if (this.disabled !== selfControl.disabled) {
        this.disabled = selfControl.disabled;
      }
      if (this.hideIfDisabled) {
        this.hidden = this.disabled;
      }

      if (data && this.id === "conductedBy") {
        this.items = [data];
        this.selectedItemId = data.value;
      }

      // Track when external data is loaded for createdBy field
      // This prevents setActiveUserAsSelectedItem from overwriting existing data
      if (data && this.id === "createdBy") {
        this.hasExternalDataLoaded = true;
        // Ensure the loaded item is in the dropdown list and selected
        if (!this.items.some((item) => item.value === data.value)) {
          this.items = [...this.items, data];
        }
        this.selectedItemId = data.value;
      }
    });

    // Warn if searching credentialed providers but not filtering by credentials
    if (
      this.config.shouldSearchCredentialedResourceProviders &&
      !this.shouldFilterByCredentials
    ) {
      console.warn(
        `[${this.id}] Configuration warning: shouldSearchCredentialedResourceProviders is true but shouldFilterByCredentials is false. This may result in unexpected behavior.`
      );
    }

    if (this.config.shouldSearchCredentialedResourceProviders) {
      this.loadAllCompanyResourceProviders();
      // setActiveUserAsSelectedItem will be called after load completes if needed
    }

    if (this.config.shouldSetToActiveUser) {
      // No full list loading, just set active user
      this.setActiveUserAsSelectedItem();
    }
  }

  ngAfterViewInit(): void {
    this.group.controls[this.id].setErrors(this.getErrors());

    // Errors can get lost when manually set
    // This is an overzealous approach to guarantee they get maintained
    this.group.valueChanges.subscribe((data) => {
      this.group.controls[this.id].setErrors(this.getErrors());
    });
  }

  private setActiveUserAsSelectedItem(): void {
    // Early exit if external data has already been loaded
    if (this.hasExternalDataLoaded) {
      return;
    }

    const selfControl = this.group.controls[this.id];
    const currentValue = selfControl.value;

    if (currentValue == null || currentValue === "") {
      const userId = this.authService.getUser().userId;

      this.loadCredentialedResourceProviderAsDropdownItem(userId).subscribe(
        (dropdownItem) => {
          if (!dropdownItem) {
            console.warn(
              `[${this.id}] Active user does not meet credential requirements or failed to load`
            );
            return;
          }

          // RE-CHECK: Has external data been loaded while we were fetching?
          if (this.hasExternalDataLoaded) {
            return;
          }

          // RE-CHECK: Has the form control value been set while we were loading?
          const currentValueAfterLoad = this.group.controls[this.id].value;
          if (
            currentValueAfterLoad != null &&
            currentValueAfterLoad !== "" &&
            typeof currentValueAfterLoad === "object" &&
            currentValueAfterLoad.value !== undefined
          ) {
            // Existing data was loaded during async operation - don't overwrite
            // Ensure the existing value is displayed in the dropdown
            if (
              !this.items.some(
                (item) => item.value === currentValueAfterLoad.value
              )
            ) {
              this.items = [...this.items, currentValueAfterLoad];
            }
            this.selectedItemId = currentValueAfterLoad.value;
            return;
          }

          // Safe to set the active user as the selected item
          this.group.controls[this.id].setValue(dropdownItem);
          this.selectedItemId = dropdownItem.value;

          // Ensure the item is in the dropdown list
          if (!this.items.some((item) => item.value === dropdownItem.value)) {
            this.items = [...this.items, dropdownItem];
          }
        }
      );
    }
  }

  private loadCredentialedResourceProviderAsDropdownItem(
    userId: number
  ): Observable<DropdownItem | null> {
    return this.resourceManagementService
      .getCredentialedResourceByExternalId(userId)
      .pipe(
        map((credentialedResource) => {
          // Format the display label
          const date = new Date(this.config.assessmentStartDate);
          const label = credentialedResource.findDisplayName(date);

          // Return as DropdownItem
          return {
            label: label,
            value: credentialedResource.cognitoUserId,
          };
        }),
        catchError((error) => {
          console.error(
            `[${this.id}] Error loading credentialed resource:`,
            error
          );
          return of(null);
        })
      );
  }

  /**
   * Helper method to extract credentials string from CredentialedResource
   * Filters to only active credentials that are in the ALLOWED_CREDENTIALS list
   */
  private extractCredentialsString(credentialedResource: any): string {
    if (
      !credentialedResource.credentials ||
      credentialedResource.credentials.length === 0
    ) {
      return "";
    }

    const operatingAsCredentials = credentialedResource.credentials.filter(
      (cred) => cred.operatingAs
    );

    const credentialTypes = operatingAsCredentials.map((cred) => cred.type);

    const activeCredentials = credentialTypes.filter((type) =>
      this.ALLOWED_CREDENTIALS.includes(type)
    );

    return activeCredentials.join(", ");
  }

  private composeValidators() {
    const validators = [];
    if (this.config.rules) {
      if (this.config.rules.required) {
        this.required = this.config.rules.required;
        validators.push(Validators.required);
      }

      if (this.config.rules.hideIfDisabled) {
        this.hideIfDisabled = this.config.rules.hideIfDisabled;
      }
    }

    const composedValidators: ValidatorFn = Validators.compose(validators);
    const selfControl = this.group.controls[this.id];
    selfControl.setValidators(composedValidators);
  }

  getErrors(): ValidationErrors {
    let errors = {};
    if (
      this.required &&
      (undefined === this.getCurrentValue() || null === this.getCurrentValue())
    ) {
      errors["required"] = true;
    }

    return Object.keys(errors).length == 0 ? undefined : errors;
  }

  getCurrentValue(): number | null {
    return this.group.controls[this.id].value;
  }

  /**
   * Load all providers on component initialization
   */
  private loadAllCompanyResourceProviders(): void {
    this.isLoading = true;
    const sortOptions = new SortOptions("lastName", "asc");

    this.resourceManagementService
      .getCompanyResourceProvidersAndNonProviders(0, 2000, "", sortOptions)
      .pipe(
        catchError((error) => {
          console.error("Error loading providers:", error);
          this.isLoading = false;
          return of({ content: [] });
        })
      )
      .subscribe((response) => {
        // Filter to only valid providers (system users with allowed credentials)
        // Then map to DropdownItem format
        this.items = response[0].content
          .filter((provider: CompanyResourceSummary) =>
            this.isValidProvider(provider)
          )
          .map((provider: CompanyResourceSummary) => ({
            label: this.formatProviderDisplay(provider),
            value: provider.externalId,
          }));
        this.isLoading = false;
      });
  }

  /**
   * Check if provider meets all filtering criteria.
   *
   * When shouldFilterByCredentials is false:
   * - Only checks if provider is a system user
   *
   * When shouldFilterByCredentials is true:
   * - Must be a system user
   * - Must have at least one allowed credential (MD, DO, FNP-BC, ANP-BC, ACHPN, CRNP, APRN)
   */
  private isValidProvider(provider: CompanyResourceSummary): boolean {
    // If credential filtering is disabled, accept any system user
    if (!this.shouldFilterByCredentials) {
      return provider.isSystemUser;
    }

    // Must be a system user
    if (!provider.isSystemUser) {
      return false;
    }

    // Must have credentials
    if (
      !provider.hasCredentials ||
      !provider.credentials ||
      provider.credentials.trim() === ""
    ) {
      return false;
    }

    // Parse credentials string and check for allowed types
    const credentialsList = provider.credentials
      .split(",")
      .map((c) => c.trim());

    return credentialsList.some((credential) =>
      this.isAllowedCredential(credential)
    );
  }

  /**
   * Check if a credential matches any allowed credential type.
   * Performs case-insensitive and hyphen-insensitive comparison to handle
   * inconsistent credential formats from the database (e.g., "FNP-BC" should
   * match "FNPBC" in ALLOWED_CREDENTIALS).
   */
  private isAllowedCredential(credential: string): boolean {
    const normalizedCredential = this.normalizeCredential(credential);

    return this.ALLOWED_CREDENTIALS.some(
      (allowed) => this.normalizeCredential(allowed) === normalizedCredential
    );
  }

  /**
   * Normalize a credential string for comparison purposes only.
   * Removes hyphens and converts to uppercase to ensure consistent matching.
   * Does not mutate the original value.
   */
  private normalizeCredential(credential: string): string {
    return credential.replace(/-/g, "").toUpperCase();
  }

  /**
   * Format provider for display: "LastName, FirstName MiddleInitial (Credentials)"
   */
  formatProviderDisplay(provider: CompanyResourceSummary): string {
    let display = `${provider.lastName}, ${provider.firstName}`;

    if (provider.middleInitial) {
      display += ` ${provider.middleInitial}`;
    }

    if (provider.credentials) {
      display += ` (${provider.credentials})`;
    }

    return display;
  }

  /**
   * Custom search function for ng-select that searches the formatted display string
   * This allows searching by name AND credentials (e.g., "CLIN", "MD", etc.)
   */
  customSearchFn = (term: string, item: DropdownItem): boolean => {
    if (!term) {
      return true;
    }
    const searchTerm = term.toLowerCase();
    return item.label.toLowerCase().includes(searchTerm);
  };

  /**
   * Handle provider selection change from ng-select
   */
  onItemChange(dropdownItem: DropdownItem): void {
    const externalId = dropdownItem?.value;
    if (externalId) {
      const selectedItem = this.items.find((item) => item.value === externalId);
      if (selectedItem) {
        this.group.controls[this.id].setValue(selectedItem);
        this.group.controls[this.id].markAsTouched();
      }
    }
  }

  /**
   * Handle clear event from ng-select
   */
  onClear(): void {
    this.group.controls[this.id].setValue(null);
    this.group.controls[this.id].markAsTouched();
  }
}



// WEBPACK FOOTER //
// ./src/components/configurable/inputs/fixed-label-search-select/fixed-label-search-select.component.ts